import { RoundedButton } from '@/src/components/Button'
import LocalizedLink from '@/src/components/LocalizedLink'
import { Heading } from '@/src/components/Typography'
import Paragraph from '@/src/components/Typography/Paragraph'
import useCurrencyFormatter from '@/src/hooks/useCurrencyFormatter'
import { useGetSettingsQuery } from '@/src/services/config'
import {
  useGetPlayerQuery,
  useResetRealityCheckMutation,
} from '@/src/services/player'
import { PlayerBalanceType } from '@/src/services/types'
import { RealityCheckType } from '@/src/services/types/limits'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

type RealityCheckProps = {
  closeEvent: () => void
  balance?: PlayerBalanceType
  providerRCEnabled: boolean
  realityCheck: RealityCheckType | undefined
  refetch: () => void
}

const RealityCheck = ({
  closeEvent,
  balance,
  providerRCEnabled,
  realityCheck,
  refetch,
}: RealityCheckProps) => {
  const { t } = useTranslation(['common', 'compliance'])
  const { data: userSettings, refetch: refetchSettings } = useGetSettingsQuery()
  const { data: user } = useGetPlayerQuery()
  const [resetRealityCheck] = useResetRealityCheckMutation()

  const [time, setTime] = useState(0)

  const close = () => {
    resetRealityCheck()
    closeEvent()
  }
  const startTime = useMemo(
    () =>
      Math.abs((userSettings?.serviceTime ?? 0) - (user?.lastLoginTime ?? 0)),
    [userSettings, user],
  )

  useEffect(() => {
    if (balance?.realityCheckReached) {
      if (!providerRCEnabled && realityCheck?.realityCheck) {
        refetch()
      }
      refetchSettings()
    }
  }, [balance, refetch, refetchSettings, providerRCEnabled, realityCheck])

  useEffect(() => {
    setTime(startTime)
  }, [startTime])

  useEffect(() => {
    let interval: ReturnType<typeof setTimeout> | null = null
    interval = setInterval(() => {
      setTime(time + 60000)
    }, 60000)
    return () => {
      interval && clearInterval(interval)
    }
  }, [time, setTime])
  const { formatter } = useCurrencyFormatter({
    minPrecision: 0,
  })
  const minutes: number = Math.round((time / (1000 * 60)) % 60)
  const hours: number = Math.floor((time / (1000 * 60 * 60)) % 24)
  const hoursString = hours < 10 ? '0' + hours : hours
  const minutesString = minutes < 10 ? '0' + minutes : minutes
  const played = `${hoursString}:${minutesString}`
  const bets = realityCheck?.realityCheckBets || 0
  const results = (realityCheck?.realityCheckWins || 0) - bets

  return (
    <div
      data-testid='reality-check-modal'
      className={`relative text-center text-sm sm:px-12 sm:py-4`}
    >
      {realityCheck?.realityCheck && (
        <>
          <Heading level={1}>
            {t('reality-check.header', { ns: 'compliance' })}
          </Heading>
          <Paragraph className='mb-3'>
            {t('reality-check.body', {
              ns: 'compliance',
              played: played,
              betted: formatter.format(bets),
              results: formatter.format(results),
            })}
          </Paragraph>
          <RoundedButton className='mb-3' type='primary' onClick={close}>
            {t('reality-check.continue-cta', {
              ns: 'compliance',
            })}
          </RoundedButton>
          <Paragraph className='mb-0'>
            <LocalizedLink
              href='/'
              passHref
              className='font-extrabold text-white underline'
            >
              {t('reality-check.exit-cta', { ns: 'compliance' })}
            </LocalizedLink>
          </Paragraph>
        </>
      )}
    </div>
  )
}

export default RealityCheck
